<template>
  <div class="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 relative overflow-hidden">
    <BackgroundElements />

    <div class="relative z-10 flex items-center justify-center min-h-[calc(100vh-120px)] px-4 sm:px-6 lg:px-8 sm:-mt-12">
      <div class="w-full max-w-sm sm:max-w-md lg:max-w-lg mx-auto">
        <div class="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <div class="flex justify-center mb-4 sm:mb-6">
            <img src="/images/vamaship-logo.png" alt="Vamaship Logo" style="width: 180px; height: auto;" />
          </div>

          <div class="text-center mb-4 sm:mb-6">
            <h1 class="text-xl sm:text-2xl font-extrabold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              {{ greeting }}
            </h1>
            <p class="mt-1 text-xs sm:text-sm text-gray-500">Welcome to Vamaship — sign in to continue.</p>
          </div>

          <div v-if="authStore.error" class="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-4 w-4 sm:h-5 sm:w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-2 sm:ml-3">
                <p class="text-xs sm:text-sm text-red-800">{{ authStore.error }}</p>
              </div>
            </div>
          </div>

          <!-- Step 1: enter identifier -->
          <form v-if="!otpSent" @submit.prevent.stop="handleRequestOtp" class="space-y-4 sm:space-y-6">
            <div class="relative">
              <label class="block text-sm font-medium text-gray-700 mb-2">Email or Mobile</label>
              <input
                type="text"
                v-model.trim="identifier"
                @keydown.enter.prevent.stop="handleRequestOtp"
                :class="[
                  'w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors text-sm sm:text-base',
                  errors.identifier ? 'border-red-300 bg-red-50' : 'border-gray-300'
                ]"
                placeholder="Enter your email or 10-digit mobile"
                required
              />
              <p v-if="errors.identifier" class="mt-1 text-xs sm:text-sm text-red-600">{{ errors.identifier }}</p>
              <p class="mt-1 text-xs text-gray-500">We'll send a one-time code to verify it's you.</p>
            </div>

            <button
              type="submit"
              :disabled="authStore.isLoading"
              :class="[
                'w-full py-2 sm:py-3 px-4 rounded-lg font-medium transition-colors text-sm sm:text-base',
                authStore.isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'
              ]"
            >
              <span v-if="authStore.isLoading" class="flex items-center justify-center">
                <SpinnerLoader size="sm" class="mr-3" />
                Sending OTP...
              </span>
              <span v-else>Send OTP</span>
            </button>
          </form>

          <!-- Step 2: enter OTP -->
          <form v-else @submit.prevent.stop="handleVerifyOtp" class="space-y-4 sm:space-y-6">
            <div class="relative">
              <label class="block text-sm font-medium text-gray-700 mb-2">Enter OTP</label>
              <p class="mb-2 text-xs sm:text-sm text-gray-500">
                Sent to <span class="font-medium text-gray-700">{{ identifier }}</span>.
                <button type="button" @click="changeIdentifier" class="text-purple-600 hover:text-purple-800 ml-1">Change</button>
              </p>
              <OtpInput v-model="otp" :length="6" @complete="handleVerifyOtp" />
              <p v-if="errors.otp" class="mt-2 text-xs sm:text-sm text-red-600 text-center">{{ errors.otp }}</p>
            </div>

            <button
              type="submit"
              :disabled="authStore.isLoading"
              :class="[
                'w-full py-2 sm:py-3 px-4 rounded-lg font-medium transition-colors text-sm sm:text-base',
                authStore.isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'
              ]"
            >
              <span v-if="authStore.isLoading" class="flex items-center justify-center">
                <SpinnerLoader size="sm" class="mr-3" />
                Verifying...
              </span>
              <span v-else>Verify &amp; Sign In</span>
            </button>

            <div class="text-center">
              <button
                type="button"
                @click="handleResendOtp"
                :disabled="resendCooldown > 0 || authStore.isLoading"
                class="text-xs sm:text-sm text-purple-600 hover:text-purple-800 disabled:text-gray-400"
              >
                {{ resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP' }}
              </button>
            </div>
          </form>

          <div class="relative my-4 sm:my-6">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300"></div>
            </div>
            <div class="relative flex justify-center text-xs sm:text-sm">
              <span class="px-2 bg-white text-gray-500">Or Sign in with</span>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-3">
            <GoogleSignIn
              :is-loading="authStore.isLoading"
              @googleSignIn="handleGoogleSignIn"
              @googleError="handleGoogleError"
            />
          </div>

          <div class="text-center mt-4 sm:mt-6">
            <p class="text-xs sm:text-sm text-gray-600">
              Don't have an account?
              <router-link to="/signup" class="text-purple-600 hover:text-purple-800 font-semibold">
                Sign Up Now
              </router-link>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import GoogleSignIn from '@/components/common/GoogleSignIn.vue'
import SpinnerLoader from '@/components/common/SpinnerLoader.vue'
import BackgroundElements from '@/components/common/BackgroundElements.vue'
import OtpInput from '@/components/common/OtpInput.vue'

const route = useRoute()
const authStore = useAuthStore()

const identifier = ref('')
const otp = ref('')
const otpSent = ref(false)
const resendCooldown = ref(0)
let cooldownTimer: ReturnType<typeof setInterval> | null = null

const errors = reactive({
  identifier: '',
  otp: ''
})

const greeting = computed(() => {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning 👋'
  if (hour < 18) return 'Good afternoon ☀️'
  return 'Good evening 🌙'
})

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
const isMobile = (v: string) => /^[0-9]{10}$/.test(v)

const validateIdentifier = (): boolean => {
  errors.identifier = ''
  if (!identifier.value) {
    errors.identifier = 'Email or mobile is required'
    return false
  }
  if (!isEmail(identifier.value) && !isMobile(identifier.value)) {
    errors.identifier = 'Enter a valid email or 10-digit mobile number'
    return false
  }
  return true
}

const startCooldown = (seconds = 60) => {
  resendCooldown.value = seconds
  if (cooldownTimer) clearInterval(cooldownTimer)
  cooldownTimer = setInterval(() => {
    resendCooldown.value -= 1
    if (resendCooldown.value <= 0 && cooldownTimer) {
      clearInterval(cooldownTimer)
      cooldownTimer = null
    }
  }, 1000)
}

onUnmounted(() => {
  if (cooldownTimer) clearInterval(cooldownTimer)
})

const handleRequestOtp = async () => {
  authStore.clearError()
  if (!validateIdentifier()) return

  const result = await authStore.requestLoginOtp(identifier.value)
  if (result.success) {
    otpSent.value = true
    otp.value = ''
    errors.otp = ''
    startCooldown()
  }
}

const handleResendOtp = async () => {
  if (resendCooldown.value > 0) return
  authStore.clearError()
  const result = await authStore.requestLoginOtp(identifier.value)
  if (result.success) startCooldown()
}

const handleVerifyOtp = async () => {
  authStore.clearError()
  errors.otp = ''
  if (!/^[0-9]{4,6}$/.test(otp.value)) {
    errors.otp = 'Enter the OTP you received'
    return
  }
  await authStore.verifyLoginOtp(identifier.value, otp.value)
  // Redirection on success is handled by the auth store.
}

const changeIdentifier = () => {
  otpSent.value = false
  otp.value = ''
  errors.otp = ''
  authStore.clearError()
  if (cooldownTimer) {
    clearInterval(cooldownTimer)
    cooldownTimer = null
  }
  resendCooldown.value = 0
}

const handleGoogleSignIn = async (credential: string) => {
  const res = await authStore.socialLoginWithGoogle({
    credential,
    reference: route.query.reference?.toString() ?? null,
    utm_medium: route.query.utm_medium?.toString() ?? null,
    utm_campaign: route.query.utm_campaign?.toString() ?? null,
    utm_source: route.query.utm_source?.toString() ?? null,
  })
  if (!res.success) {
    console.error(res.error)
  }
}

const handleGoogleError = (message: string) => {
  console.error('Google Sign-In error:', message)
}
</script>
